// Geometry helpers bridging Terra Draw (GeoJSON polygons) and the persisted geofence
// format, which keeps circles as `{ radius, center }` (consumed by the backend).

import maplibregl from 'maplibre-gl'

export interface LatLng {
  lat: number
  lng: number
}

export interface PolygonGeometry {
  type: 'Polygon'
  coordinates: number[][][]
}

export interface CircleGeometry {
  radius: number
  center: LatLng
}

type LngLat = [number, number]

const EARTH_RADIUS_M = 6371000

const toRad = (deg: number) => (deg * Math.PI) / 180
const toDeg = (rad: number) => (rad * 180) / Math.PI

export function haversineMeters(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

// Destination point at a distance/bearing from origin, returned as GeoJSON [lng, lat].
function destinationPoint(center: LatLng, distanceM: number, bearingRad: number): [number, number] {
  const angular = distanceM / EARTH_RADIUS_M
  const lat1 = toRad(center.lat)
  const lng1 = toRad(center.lng)

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angular) + Math.cos(lat1) * Math.sin(angular) * Math.cos(bearingRad)
  )
  const lng2 = lng1 + Math.atan2(
    Math.sin(bearingRad) * Math.sin(angular) * Math.cos(lat1),
    Math.cos(angular) - Math.sin(lat1) * Math.sin(lat2)
  )

  return [toDeg(lng2), toDeg(lat2)]
}

// Approximates a circle as a closed GeoJSON polygon ring (for display/editing).
export function circleToPolygon(center: LatLng, radiusM: number, steps = 64): PolygonGeometry {
  const ring: [number, number][] = []
  for (let i = 0; i < steps; i++) {
    ring.push(destinationPoint(center, radiusM, (2 * Math.PI * i) / steps))
  }
  ring.push(ring[0])
  return {type: 'Polygon', coordinates: [ring]}
}

// Reduces a (circle-shaped) polygon back to `{ radius, center }` for persistence.
export function polygonToCircle(geometry: PolygonGeometry): CircleGeometry {
  const ring = geometry.coordinates[0]
  const points = ring.slice(0, -1) // drop the closing duplicate
  const sum = points.reduce((acc, [lng, lat]) => ({lat: acc.lat + lat, lng: acc.lng + lng}), {lat: 0, lng: 0})
  const center: LatLng = {lat: sum.lat / points.length, lng: sum.lng / points.length}
  const radius = haversineMeters(center, {lat: ring[0][1], lng: ring[0][0]})
  return {radius, center}
}

export function boundsFromGeofenceGeometry(geometry: PolygonGeometry | CircleGeometry): maplibregl.LngLatBounds | null {
  const polygon = 'radius' in geometry
      ? circleToPolygon(geometry.center, geometry.radius)
      : geometry

  const coordinates: LngLat[] = polygon.coordinates
      .flat()
      .filter((coordinate): coordinate is LngLat => coordinate.length >= 2)
      .map(([lng, lat]) => [lng, lat])
  if (!coordinates.length) {
    return null
  }

  return coordinates.reduce(
      (bounds, coordinate) => bounds.extend(coordinate),
      new maplibregl.LngLatBounds(coordinates[0], coordinates[0])
  )
}
